<template>
    <BaseSidebar negatePaddingTop :value="value" @input="toggleSidebar">
        <div
            class="d-flex flex-column align-items-stretch text-center flex-grow"
            style="max-width: 100%; justify-content: space-evenly"
        >
            <div slot="top" class="p-2" style="height: 57px">
                <LocaleDropdown
                    ref="localeDropdown"
                    noModal
                    @open="toggleLocaleList"
                    @close="toggleLocaleList"
                />
            </div>
            <b-collapse :visible="showLocaleList" id="sidebarLocaleList">
                <div class="sidebar-locale-list mb-5">
                    <div v-for="l in locales" :key="l.locale" @click="onChangeLocale(l)">
                        <div class="sidebar-locale-item clickable d-flex align-items-center py-2">
                            <FlagIcon
                                class="mr-3"
                                :country="l.countryCode"
                                size="big"
                                avatar
                                avatarSize="small"
                            />
                            <h5 class="mb-0">
                                {{ $t(`components.builders.localeDropdown.languages.${l.locale}`) }}
                            </h5>
                        </div>
                        <Divider />
                    </div>
                </div>
            </b-collapse>

            <template>
                <div class="top-section">
                    <div v-if="isAuthenticated && showProfileBanner" class="top-section-userinfo">
                        <div class="w-100 d-flex justify-content-center align-items-center">
                            <Avatar class="top-section-avatar" :image="profileImage">{{
                                firstLetterInName
                            }}</Avatar>
                        </div>
                        <div class="py-3">
                            <h6 class="caption mute">{{ email }}</h6>
                        </div>
                    </div>
                    <div class="h-100" v-else>
                        <div class="image-banner header-background" alt="banner" />
                    </div>
                </div>
            </template>
            <Divider />

            <b-collapse :visible="!showLocaleList" id="sidebar-action-list">
                <div v-for="action in actions" :key="action.label">
                    <div
                        v-if="!action.show || action.show()"
                        class="clickable py-2 px-2 mt-2 d-flex align-items-center"
                        @click="pushPage(action.url, action.external, action.newTab)"
                    >
                        <BaseIcon :name="action.icon" />
                        <h5 class="inherit-color link text-left mb-0 ml-2">
                            {{ $t(`components.navigation.sidebar.${action.value}`) }}
                        </h5>
                    </div>
                </div>
            </b-collapse>

            <div class="flex-grow" />

            <div
                v-if="hasLogin"
                class="clickable py-2 px-2 my-2 d-flex align-items-center"
                @click="isAuthenticated ? authService.logOut() : signIn()"
            >
                <BaseIcon :name="isAuthenticated ? 'sign-out-alt' : 'sign-in-alt'" />
                <h5 class="inherit-color link text-left mb-0 ml-2">
                    {{
                        isAuthenticated
                            ? $t('components.navigation.sidebar.logOut')
                            : $t('components.navigation.sidebar.signIn')
                    }}
                </h5>
            </div>
        </div>
    </BaseSidebar>
</template>

<script>
import AuthService from '~/services/AuthService';
import I18nService from '~/services/I18nService';
import LocaleDropdown from './LocaleDropdown';
import {
    SHOW_JOBS,
    SHOW_COMPANIES,
    HAS_LOGIN,
    HAS_MY_PAGE,
    HAS_MASTERCLASSES,
    HAS_VFAIRS,
    HAS_COUNTRY_GUIDES,
    HAS_HAIGUI_LINK,
    SHOW_PROFILE_BANNER,
} from '~/router';

import localization from '~/localization';

// Data
import locales from '~/data/locales.json';

export default {
    name: 'NavigationSidebar',
    components: { LocaleDropdown },
    props: {
        value: Boolean, // Show sidebar
    },

    data: function() {
        return {
            locales: locales,
            authService: AuthService,
            showLocaleList: false,
            hasLogin: HAS_LOGIN,
            showProfileBanner: SHOW_PROFILE_BANNER,
            actions: [
                {
                    value: 'myPage',
                    icon: 'user',
                    url: this.$URLS.mypage,
                    show: () => HAS_MY_PAGE,
                },
                {
                    value: 'myProfile',
                    icon: 'user',
                    url: this.$URLS.spotlight,
                    show: () => localization.isCompanyPlatform,
                },
                {
                    value: 'virtualFairs',
                    icon: 'globe-americas',
                    url: '/',
                    show: () => localization.isCompanyPlatform,
                },
                {
                    value: 'findJobs',
                    icon: 'briefcase',
                    url: this.$URLS.findjobs,
                    show: () => SHOW_JOBS,
                },
                {
                    value: 'findCompanies',
                    icon: 'building',
                    url: this.$URLS.findcompanies,
                    show: () => SHOW_COMPANIES,
                },
                {
                    value: 'resources',
                    icon: 'video',
                    url: this.$URLS.resources,
                    show: () => HAS_MASTERCLASSES,
                },
                {
                    value: 'countryGuides',
                    icon: 'globe-americas',
                    url: `https://highered.global/wiki/index.php/Main_Page`,
                    external: true,
                    newTab: true,
                    show: () => HAS_COUNTRY_GUIDES,
                },
                {
                    value: 'goToHaigui',
                    icon: 'globe-asia',
                    url: `https://haigui.gethighered.global`,
                    external: true,
                    newTab: true,
                    show: () => {
                        if (!HAS_HAIGUI_LINK) {
                            return false;
                        }
                        /*
                        if (I18nService.getLocale() === 'cn') {
                            return true;
                        }
                        let userLanguage =
                            (navigator.languages && navigator.languages[0]) || // Chrome / Firefox
                            navigator.language || // All browsers
                            navigator.userLanguage; // IE <= 10

                        return userLanguage.includes('zh');
                        */
                        return true;
                    },
                },
                {
                    value: 'virtualFairs',
                    icon: 'globe-americas',
                    url: this.$URLS.vfairs,
                    show: () => this.$interfaces.vfairModule.getHasVFairs(),
                },
            ],
        };
    },
    computed: {
        profile() {
            return this.$interfaces.user.me.get({ reactive: true });
        },
        name() {
            return this.data().firstName && this.data().lastName
                ? (this.data().firstName + ' ' || '') + (this.data().lastName || '')
                : '';
        },
        email() {
            return this.data().email || '';
        },
        profileImage() {
            return this.data().image;
        },
        firstLetterInName() {
            return this.data().firstLetterInName;
        },
        isAuthenticated() {
            return this.$interfaces.logInModule.isLoggedIn();
        },
    },
    methods: {
        toggleSidebar(value) {
            this.$emit('input', value);
        },
        pushPage(page, external, newTab) {
            if (external) {
                newTab ? window.open(page) : window.open(page, '_self');
            } else {
                this.$router.push(page);
            }

            this.toggleSidebar(false);
        },
        data() {
            return (this.profile || {}).data;
        },
        loading() {
            return this.profile.loading;
        },
        error() {
            return this.profile.error;
        },
        signIn() {
            this.$interfaces.logInModule.resetDisabledPopup();
            this.$interfaces.logInModule.toggleOpen();
            this.toggleSidebar();
        },
        toggleLocaleList() {
            this.showLocaleList = !this.showLocaleList;
        },
        onChangeLocale(localeItem) {
            if (this.$refs.localeDropdown) {
                this.$refs.localeDropdown.onChange(localeItem);
            }
        },
    },
};
</script>

<style scoped>
.no_wrap_elipsis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.link {
    font-weight: 500 !important;
}

.top-section {
    width: 100%;
    background-color: white;

    height: 155px;
    position: relative;
    border-top: 3px solid var(--primary);
}

.top-section-userinfo {
    width: 100%;
    padding: 0 10px;
    position: absolute;
    top: -28px;
    height: 56px;
}

.top-section-avatar {
    box-sizing: border-box;
    height: 124px !important;
    width: 124px !important;
    border: 3px solid var(--primary);
    border-radius: 50%;
}

.image-banner {
    width: 100%;
    max-height: 100%;
    object-fit: cover;
    box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.2);

    height: 400px;
    object-fit: scale-down;
    background-size: cover;
    background-position: center;
}

.sidebar-locale-list {
    border-top: 3px solid var(--primary);
}

.sidebar-locale-item {
    padding-left: 12px;
    padding-right: 12px;
}

@media only screen and (max-height: 450px) {
    .hide-on-low-height {
        display: none;
    }
}
</style>
